import { useState, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { countries, getDefaultCountry, validatePhone, cleanPhoneNumber, formatPhoneDisplay, type Country } from "@/utils/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, ChevronDown, Phone } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface PhoneStepProps {
  onSubmit: (countryCode: string, mobile: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  showGuestOption?: boolean;
  onContinueAsGuest?: () => void;
  initialMobile?: string;
  initialCountry?: Country;
}

export default function PhoneStep({
  onSubmit,
  loading,
  error,
  showGuestOption,
  onContinueAsGuest,
  initialMobile = "",
  initialCountry,
}: PhoneStepProps) {
  const { language } = useLanguage();
  const isAr = language === "ar";

  const [country, setCountry] = useState<Country>(initialCountry || getDefaultCountry());
  const [mobile, setMobile] = useState(initialMobile);
  const [countryOpen, setCountryOpen] = useState(false);
  const [search, setSearch] = useState("");

  const cleaned = cleanPhoneNumber(mobile);
  const isValid = validatePhone(cleaned, country);

  const popularCountries = useMemo(() => countries.filter((c) => c.popular), []);
  const allCountries = useMemo(() => {
    if (!search) return countries.filter((c) => !c.popular);
    const q = search.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.nameAr.includes(q) ||
        c.dialCode.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [search]);

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw.length <= country.maxLength) {
      setMobile(raw);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    await onSubmit(country.dialCode, cleaned);
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="text-center space-y-2">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Phone className="w-7 h-7 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {isAr ? "التحقق من رقم الهاتف" : "Verify your phone number"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isAr ? "سنرسل رمز تحقق إلى هاتفك" : "We'll send a verification code to your phone"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Phone input row */}
        <div className="flex gap-2">
          {/* Country selector */}
          <Popover open={countryOpen} onOpenChange={setCountryOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="shrink-0 gap-1.5 px-3 min-w-[110px] justify-between"
              >
                <span className="text-lg">{country.flag}</span>
                <span className="text-sm font-medium">{country.dialCode}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={isAr ? "ابحث عن دولة..." : "Search country..."}
                    className="ps-8 h-9"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto p-1">
                {!search && (
                  <>
                    <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      {isAr ? "الأكثر شيوعاً" : "Popular"}
                    </p>
                    {popularCountries.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm hover:bg-muted transition-colors"
                        onClick={() => {
                          setCountry(c);
                          setMobile("");
                          setCountryOpen(false);
                          setSearch("");
                        }}
                      >
                        <span className="text-lg">{c.flag}</span>
                        <span className="flex-1 text-start">{isAr ? c.nameAr : c.name}</span>
                        <span className="text-muted-foreground">{c.dialCode}</span>
                      </button>
                    ))}
                    <div className="border-t border-border my-1" />
                    <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      {isAr ? "جميع الدول" : "All Countries"}
                    </p>
                  </>
                )}
                {(search ? allCountries : countries.filter((c) => !c.popular)).map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm hover:bg-muted transition-colors"
                    onClick={() => {
                      setCountry(c);
                      setMobile("");
                      setCountryOpen(false);
                      setSearch("");
                    }}
                  >
                    <span className="text-lg">{c.flag}</span>
                    <span className="flex-1 text-start">{isAr ? c.nameAr : c.name}</span>
                    <span className="text-muted-foreground">{c.dialCode}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Mobile input */}
          <Input
            type="tel"
            inputMode="numeric"
            placeholder={country.placeholder}
            value={mobile ? formatPhoneDisplay(mobile) : ""}
            onChange={handleMobileChange}
            className="flex-1 text-base tracking-wider"
            dir="ltr"
            autoFocus
          />
        </div>

        {/* Inline error */}
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        {mobile && !isValid && mobile.length >= 3 && (
          <p className="text-sm text-destructive">
            {isAr ? "يرجى إدخال رقم هاتف صحيح" : "Please enter a valid phone number"}
          </p>
        )}

        {/* Submit */}
        <Button type="submit" className="w-full" disabled={!isValid || loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            isAr ? "التالي" : "Next"
          )}
        </Button>
      </form>

      {/* Terms */}
      <p className="text-xs text-center text-muted-foreground">
        {isAr
          ? "بالمتابعة، أنت توافق على شروط الاستخدام وسياسة الخصوصية"
          : "By continuing, you agree to our Terms of Service and Privacy Policy"}
      </p>

      {/* Guest option */}
      {showGuestOption && onContinueAsGuest && (
        <button
          type="button"
          onClick={onContinueAsGuest}
          className="w-full text-center text-sm text-primary hover:underline"
        >
          {isAr ? "المتابعة كزائر" : "Continue as Guest"}
        </button>
      )}
    </div>
  );
}
